/**
 * Application Insights Telemetry
 *
 * Initializes the Azure Application Insights SDK for the React frontend.
 * The connection string is read from runtime config so it can be set per
 * environment without rebuilding the bundle.
 *
 * Runtime config key: "appInsightsConnectionString"
 *
 * Usage:
 *   import { initTelemetry, appInsights } from './config/telemetry';
 *   await initTelemetry();          // call once during bootstrap
 *   appInsights.trackEvent(...)     // use anywhere after init
 */

import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { getConfigValue } from './runtimeConfig';

export const reactPlugin = new ReactPlugin();

let _appInsights = null;

/**
 * Initialise Application Insights.
 * Safe to call multiple times – only the first call with a valid
 * connection string will bootstrap the SDK.
 */
export const initTelemetry = () => {
  if (_appInsights) return _appInsights;

  const connectionString = getConfigValue('appInsightsConnectionString', '');
  if (!connectionString) {
    console.warn('[Telemetry] No appInsightsConnectionString configured – telemetry disabled.');
    return null;
  }

  _appInsights = new ApplicationInsights({
    config: {
      connectionString,
      extensions: [reactPlugin],
      enableAutoRouteTracking: true,
      disableFetchTracking: false,
      enableCorsCorrelation: true,
      enableRequestHeaderTracking: true,
      enableResponseHeaderTracking: true,
    },
  });

  _appInsights.loadAppInsights();
  _appInsights.trackPageView();

  // Capture unhandled promise rejections (mirrors backend global exception handling)
  window.addEventListener('unhandledrejection', (event) => {
    _appInsights.trackException({
      exception: event.reason instanceof Error ? event.reason : new Error(String(event.reason)),
      properties: { type: 'unhandledrejection' },
    });
  });

  return _appInsights;
};

/**
 * Convenience getter – returns the SDK instance (or null when telemetry
 * is not configured).
 */
export const getAppInsights = () => _appInsights;
